import type { TourListItem } from '@/types/tour'

type LibraryMapVisibilityOptions = {
  tours: TourListItem[]
  showOnlyUploadedBatch: boolean
  uploadedBatchIds: Set<string>
  isTrackDrawingMode?: boolean
  isTrackEditMode: boolean
  selectedTourIds: Set<string>
  editingTrackTourId: string | null
  editingTrackClientId: string | null
  showAllTours?: boolean
  clientSideFilteredIds?: Set<string> | null
  pinnedTourIds?: Set<string>
}

export function getLibraryToursToRender({
  tours,
  showOnlyUploadedBatch,
  uploadedBatchIds,
  isTrackDrawingMode = false,
  isTrackEditMode,
  selectedTourIds,
  editingTrackTourId,
  showAllTours,
  clientSideFilteredIds,
  pinnedTourIds,
}: LibraryMapVisibilityOptions): TourListItem[] {
  void editingTrackTourId
  const baseTours = showOnlyUploadedBatch
    ? tours.filter((tour) => uploadedBatchIds.has(tour.clientId))
    : tours

  const applyClientFilter = (list: TourListItem[]) => {
    if (clientSideFilteredIds == null) return list
    return list.filter((tour) => clientSideFilteredIds.has(tour.clientId))
  }

  // Always include pinned tours (newly drawn routes) regardless of any filter.
  const injectPinned = (list: TourListItem[]) => {
    if (!pinnedTourIds || pinnedTourIds.size === 0) return list
    const presentIds = new Set(list.map((t) => t.clientId))
    const missing = tours.filter(
      (t) => pinnedTourIds.has(t.clientId) && !presentIds.has(t.clientId),
    )
    return missing.length > 0 ? [...missing, ...list] : list
  }

  // Always include selected tours regardless of any filter.
  const injectSelected = (list: TourListItem[]) => {
    if (selectedTourIds.size === 0) return list
    const presentIds = new Set(list.map((t) => t.clientId))
    const missing = tours.filter(
      (t) => selectedTourIds.has(t.clientId) && !presentIds.has(t.clientId),
    )
    return missing.length > 0 ? [...missing, ...list] : list
  }

  if (showAllTours) {
    return injectSelected(injectPinned(applyClientFilter(baseTours)))
  }

  // Route mode keeps the library context visible behind the foreground drawing/edit layers.
  // Selection is deliberately not used as a visibility filter while planning a route.
  if (isTrackDrawingMode || isTrackEditMode) {
    return injectPinned(applyClientFilter(baseTours))
  }

  const selectedTours = baseTours.filter((tour) => selectedTourIds.has(tour.clientId))

  return injectSelected(injectPinned(applyClientFilter(selectedTours)))
}
